import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Play, Pause, SkipBack, SkipForward, Wand2, 
  RefreshCw, Check, AlertCircle, Loader2, Film, Image as ImageIcon,
  Plus, X, User, Palette, Pencil, Move, Trash2,
  Eye, EyeOff, Undo, Eraser, ChevronLeft, ChevronRight, Edit3, Upload, Sparkles, Download
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Default character colors
const DEFAULT_COLORS = ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899'];

export default function MotionLabV3() {
  // Mode: 'setup' | 'drawing' | 'preview' | 'generating' | 'final'
  const [mode, setMode] = useState('setup');
  
  // Character setup
  const [characters, setCharacters] = useState([
    { name: '', images: [], color: DEFAULT_COLORS[0] }
  ]);
  const [backgroundImages, setBackgroundImages] = useState([]);
  const [actionPrompt, setActionPrompt] = useState('');
  
  // Job state
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState('idle');
  const [startingFrame, setStartingFrame] = useState(null);
  const [startingDescription, setStartingDescription] = useState('');
  const [uploadedStartingFrame, setUploadedStartingFrame] = useState(null); // User uploaded image
  
  // Stick figure frames - includes canvas data for editing
  const [stickFrames, setStickFrames] = useState([]); 
  const [selectedFrameIndex, setSelectedFrameIndex] = useState(-1); // -1 = new frame, 0+ = editing existing
  const [editingFrameIndex, setEditingFrameIndex] = useState(null); // Which frame we're editing
  const [generatingFrames, setGeneratingFrames] = useState({}); // Track which frames are generating {index: true}
  
  // Drawing state
  const [drawingTool, setDrawingTool] = useState('freehand'); // 'freehand' | 'skeleton' | 'eraser'
  const [currentColor, setCurrentColor] = useState(DEFAULT_COLORS[0]);
  const [brushSize, setBrushSize] = useState(4);
  const [eraserSize, setEraserSize] = useState(20);
  const [onionSkinCount, setOnionSkinCount] = useState(1);
  const [showBaseImage, setShowBaseImage] = useState(true);
  const [baseImageOpacity, setBaseImageOpacity] = useState(0.3);
  
  // Skeleton joints for each character
  const [skeletons, setSkeletons] = useState({});
  
  // Canvas refs
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [canvasHistory, setCanvasHistory] = useState([]);
  
  // Preview animation in canvas
  const [canvasPreviewPlaying, setCanvasPreviewPlaying] = useState(false);
  const [canvasPreviewFrame, setCanvasPreviewFrame] = useState(0);
  const canvasPreviewRef = useRef(null);
  
  // Preview state for preview mode
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [previewFrame, setPreviewFrame] = useState(0);
  const previewIntervalRef = useRef(null);
  
  // Final video
  const [finalVideoUrl, setFinalVideoUrl] = useState(null);
  
  // Error
  const [error, setError] = useState(null);

  // Generate a single frame
  const generateSingleFrame = async (frameIndex) => {
    if (!jobId || generatingFrames[frameIndex]) return;
    
    setGeneratingFrames(prev => ({ ...prev, [frameIndex]: true }));
    
    try {
      const resp = await axios.post(`${API}/api/motion-lab/v3/job/${jobId}/generate-frame/${frameIndex}`);
      
      if (resp.data.generated_url) {
        setStickFrames(prev => prev.map((f, i) => 
          i === frameIndex 
            ? { ...f, generatedUrl: resp.data.generated_url, status: 'complete' }
            : f
        ));
      }
    } catch (err) {
      console.error('Frame generation failed:', err);
      setStickFrames(prev => prev.map((f, i) => 
        i === frameIndex 
          ? { ...f, status: 'error', error: err.message }
          : f
      ));
    } finally {
      setGeneratingFrames(prev => ({ ...prev, [frameIndex]: false }));
    }
  };

  // Download a generated frame
  const downloadGeneratedFrame = async (frameIndex) => {
    const frame = stickFrames[frameIndex];
    if (!frame?.generatedUrl) return;
    
    try {
      const response = await fetch(frame.generatedUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `frame_${frameIndex + 1}_generated.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  // Upload a custom generated frame
  const uploadGeneratedFrame = async (frameIndex, file) => {
    if (!jobId || !file) return;
    
    setGeneratingFrames(prev => ({ ...prev, [frameIndex]: true }));
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const resp = await axios.post(
        `${API}/api/motion-lab/v3/job/${jobId}/upload-generated-frame/${frameIndex}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      if (resp.data.generated_url) {
        setStickFrames(prev => prev.map((f, i) => 
          i === frameIndex 
            ? { ...f, generatedUrl: resp.data.generated_url, status: 'complete' }
            : f
        ));
      }
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setGeneratingFrames(prev => ({ ...prev, [frameIndex]: false }));
    }
  };

  // Initialize canvas with WHITE background
  const initCanvas = useCallback(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      canvas.width = 1280;
      canvas.height = 720;
      
      const context = canvas.getContext('2d');
      context.lineCap = 'round';
      context.lineJoin = 'round';
      contextRef.current = context;
      
      // WHITE background for easier drawing
      context.fillStyle = 'white';
      context.fillRect(0, 0, canvas.width, canvas.height);
      
      // Save initial state
      setCanvasHistory([canvas.toDataURL()]);
    }
  }, []);

  // Initialize canvas when entering drawing mode
  useEffect(() => {
    if (mode === 'drawing') {
      initCanvas();
    }
  }, [mode, initCanvas]);

  // Update brush settings
  useEffect(() => {
    if (contextRef.current) {
      if (drawingTool === 'eraser') {
        contextRef.current.strokeStyle = 'white';
        contextRef.current.lineWidth = eraserSize;
      } else {
        contextRef.current.strokeStyle = currentColor;
        contextRef.current.lineWidth = brushSize;
      }
    }
  }, [currentColor, brushSize, eraserSize, drawingTool]);

  // Canvas preview animation in drawing mode
  useEffect(() => {
    if (canvasPreviewPlaying && stickFrames.length > 0) {
      canvasPreviewRef.current = setInterval(() => {
        setCanvasPreviewFrame(prev => (prev + 1) % (stickFrames.length + 1));
      }, 400);
    } else {
      if (canvasPreviewRef.current) {
        clearInterval(canvasPreviewRef.current);
      }
    }
    return () => {
      if (canvasPreviewRef.current) {
        clearInterval(canvasPreviewRef.current);
      }
    };
  }, [canvasPreviewPlaying, stickFrames.length]);

  // Load frame into canvas when canvasPreviewFrame changes (during preview)
  useEffect(() => {
    if (canvasPreviewPlaying && canvasRef.current && contextRef.current) {
      const context = contextRef.current;
      const canvas = canvasRef.current;
      
      if (canvasPreviewFrame === 0) {
        // Show starting frame faded with white overlay
        context.fillStyle = 'white';
        context.fillRect(0, 0, canvas.width, canvas.height);
      } else {
        const frame = stickFrames[canvasPreviewFrame - 1];
        if (frame?.canvasData) {
          const img = new Image();
          img.onload = () => {
            context.clearRect(0, 0, canvas.width, canvas.height);
            context.drawImage(img, 0, 0, canvas.width, canvas.height);
          };
          img.src = frame.canvasData;
        }
      }
    }
  }, [canvasPreviewFrame, canvasPreviewPlaying, stickFrames]);

  // Load selected frame into canvas for viewing/editing
  const loadFrameToCanvas = useCallback((frameIndex) => {
    if (!canvasRef.current || !contextRef.current) return;
    
    const context = contextRef.current;
    const canvas = canvasRef.current;
    
    if (frameIndex === -1) {
      // Clear canvas for new frame
      context.fillStyle = 'white';
      context.fillRect(0, 0, canvas.width, canvas.height);
      setCanvasHistory([canvas.toDataURL()]);
    } else if (stickFrames[frameIndex]?.canvasData) {
      // Load existing frame
      const img = new Image();
      img.onload = () => {
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(img, 0, 0, canvas.width, canvas.height);
        setCanvasHistory([canvas.toDataURL()]);
      };
      img.src = stickFrames[frameIndex].canvasData;
    }
  }, [stickFrames]);

  // Character management
  const addCharacter = () => {
    const newColor = DEFAULT_COLORS[characters.length % DEFAULT_COLORS.length];
    setCharacters([...characters, { name: '', images: [], color: newColor }]);
  };

  const removeCharacter = (index) => {
    setCharacters(characters.filter((_, i) => i !== index));
  };

  const updateCharacter = (index, field, value) => {
    const updated = [...characters];
    updated[index][field] = value;
    setCharacters(updated);
  };

  const addCharacterImage = (charIndex, e) => {
    const files = Array.from(e.target.files);
    const updated = [...characters];
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        updated[charIndex].images.push({
          file,
          preview: event.target.result,
          url: null
        });
        setCharacters([...updated]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeCharacterImage = (charIndex, imgIndex) => {
    const updated = [...characters];
    updated[charIndex].images = updated[charIndex].images.filter((_, i) => i !== imgIndex);
    setCharacters(updated);
  };

  // Background image management
  const addBackgroundImage = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setBackgroundImages(prev => [...prev, {
          file,
          preview: event.target.result,
          url: null
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  // Upload image to S3
  const uploadImageToS3 = async (dataUrl, filename) => {
    const resp = await axios.post(`${API}/api/motion-lab/upload-image`, {
      image_data: dataUrl,
      filename: filename
    });
    return resp.data.url;
  };

  // Start the workflow - create job and generate starting frame
  const startWorkflow = async () => {
    if (!actionPrompt.trim()) {
      setError('Please enter an action description');
      return;
    }

    const validCharacters = characters.filter(c => c.name && c.images.length > 0);
    if (validCharacters.length === 0) {
      setError('Please add at least one character with reference images');
      return;
    }

    setError(null);
    setJobStatus('uploading');

    try {
      // Upload all images to S3
      const uploadedCharacters = [];
      for (const char of validCharacters) {
        const uploadedImages = [];
        for (let i = 0; i < char.images.length; i++) {
          const img = char.images[i];
          if (!img.url) {
            const url = await uploadImageToS3(img.preview, `${char.name}_ref_${i}.png`);
            uploadedImages.push(url);
          } else {
            uploadedImages.push(img.url);
          }
        }
        uploadedCharacters.push({
          name: char.name,
          image_urls: uploadedImages,
          color: char.color
        });
      }

      // Upload background images
      const uploadedBackgrounds = [];
      for (let i = 0; i < backgroundImages.length; i++) {
        const img = backgroundImages[i];
        if (!img.url) {
          const url = await uploadImageToS3(img.preview, `background_${i}.png`);
          uploadedBackgrounds.push(url);
        } else {
          uploadedBackgrounds.push(img.url);
        }
      }

      setJobStatus('creating');

      // Create the v3 job
      const createResp = await axios.post(`${API}/api/motion-lab/v3/job`, {
        characters: uploadedCharacters,
        background_urls: uploadedBackgrounds,
        action_prompt: actionPrompt
      });

      const newJobId = createResp.data.job_id;
      setJobId(newJobId);
      setJobStatus('generating_start');

      // Generate starting frame
      const startResp = await axios.post(
        `${API}/api/motion-lab/v3/job/${newJobId}/generate-starting-frame`
      );

      setStartingFrame(startResp.data.frame_url);
      setStartingDescription(startResp.data.description);
      setJobStatus('ready');
      setMode('drawing');
      setSelectedFrameIndex(-1);

    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start workflow');
      setJobStatus('error');
    }
  };

  // Start with user-uploaded image instead of AI generation
  const startWithUploadedImage = async () => {
    if (!uploadedStartingFrame) {
      setError('Please upload an image first');
      return;
    }

    const validCharacters = characters.filter(c => c.name);
    if (validCharacters.length === 0) {
      setError('Please add at least one character name');
      return;
    }

    setError(null);
    setJobStatus('uploading');

    try {
      // Upload the starting frame to S3
      const startingFrameUrl = await uploadImageToS3(uploadedStartingFrame.preview, 'starting_frame.png');
      
      // Upload character images if any
      const uploadedCharacters = [];
      for (const char of validCharacters) {
        const uploadedImages = [];
        for (let i = 0; i < char.images.length; i++) {
          const img = char.images[i];
          if (!img.url) {
            const url = await uploadImageToS3(img.preview, `${char.name}_ref_${i}.png`);
            uploadedImages.push(url);
          } else {
            uploadedImages.push(img.url);
          }
        }
        uploadedCharacters.push({
          name: char.name,
          image_urls: uploadedImages,
          color: char.color
        });
      }

      setJobStatus('creating');

      // Create job with uploaded starting frame
      const createResp = await axios.post(`${API}/api/motion-lab/v3/job`, {
        characters: uploadedCharacters,
        background_urls: [],
        action_prompt: 'User uploaded starting frame'
      });

      const newJobId = createResp.data.job_id;
      setJobId(newJobId);

      // Set the starting frame directly (skip AI generation)
      await axios.post(`${API}/api/motion-lab/v3/job/${newJobId}/set-starting-frame`, {
        frame_url: startingFrameUrl
      });

      setStartingFrame(startingFrameUrl);
      setStartingDescription('User uploaded image');
      setJobStatus('ready');
      setMode('drawing');
      setSelectedFrameIndex('new');

    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start with uploaded image');
      setJobStatus('error');
    }
  };

  // Skip directly to drawing without any starting frame
  const skipToDrawing = async () => {
    const validCharacters = characters.filter(c => c.name);
    if (validCharacters.length === 0) {
      setError('Please add at least one character name');
      return;
    }

    setError(null);
    setJobStatus('creating');

    try {
      // Upload character images if any
      const uploadedCharacters = [];
      for (const char of validCharacters) {
        const uploadedImages = [];
        for (let i = 0; i < char.images.length; i++) {
          const img = char.images[i];
          if (!img.url) {
            const url = await uploadImageToS3(img.preview, `${char.name}_ref_${i}.png`);
            uploadedImages.push(url);
          } else {
            uploadedImages.push(img.url);
          }
        }
        uploadedCharacters.push({
          name: char.name,
          image_urls: uploadedImages,
          color: char.color
        });
      }

      // Create job without starting frame
      const createResp = await axios.post(`${API}/api/motion-lab/v3/job`, {
        characters: uploadedCharacters,
        background_urls: [],
        action_prompt: 'No starting frame - user drawing from scratch'
      });

      setJobId(createResp.data.job_id);
      setStartingFrame(null);
      setStartingDescription('');
      setJobStatus('ready');
      setMode('drawing');
      setSelectedFrameIndex('new');

    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start');
      setJobStatus('error');
    }
  };

  // Drawing functions
  const startDrawing = (e) => {
    if (drawingTool === 'skeleton') return;
    if (canvasPreviewPlaying) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    contextRef.current.beginPath();
    contextRef.current.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing || drawingTool === 'skeleton') return;
    if (canvasPreviewPlaying) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    contextRef.current.lineTo(x, y);
    contextRef.current.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      contextRef.current.closePath();
      setIsDrawing(false);
      // Save to history
      const canvas = canvasRef.current;
      setCanvasHistory(prev => [...prev, canvas.toDataURL()]);
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const context = contextRef.current;
    context.fillStyle = 'white';
    context.fillRect(0, 0, canvas.width, canvas.height);
    setCanvasHistory([canvas.toDataURL()]);
  };

  const undoDrawing = () => {
    if (canvasHistory.length > 1) {
      const newHistory = canvasHistory.slice(0, -1);
      setCanvasHistory(newHistory);
      
      const img = new Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        const context = contextRef.current;
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(img, 0, 0);
      };
      img.src = newHistory[newHistory.length - 1];
    } else {
      clearCanvas();
    }
  };

  // Save current stick figure as a frame (new or update existing)
  const saveStickFrame = async () => {
    if (!jobId) return;
    
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    
    setJobStatus('saving_frame');
    
    try {
      if (editingFrameIndex !== null) {
        // Update existing frame
        const resp = await axios.post(`${API}/api/motion-lab/v3/job/${jobId}/update-stick-frame/${editingFrameIndex}`, {
          stick_figure_data: dataUrl
        });
        
        setStickFrames(prev => prev.map((f, i) => 
          i === editingFrameIndex 
            ? { ...f, stickUrl: resp.data.stick_figure_url, canvasData: dataUrl, generatedUrl: null, status: 'pending' }
            : f
        ));
        setEditingFrameIndex(null);
      } else {
        // Add new frame
        const resp = await axios.post(`${API}/api/motion-lab/v3/job/${jobId}/add-stick-frame`, {
          stick_figure_data: dataUrl
        });
        
        setStickFrames(prev => [...prev, {
          index: resp.data.frame_index,
          stickUrl: resp.data.stick_figure_url,
          canvasData: dataUrl, // Store canvas data for editing
          generatedUrl: null,
          status: 'pending'
        }]);
      }
      
      clearCanvas();
      setSelectedFrameIndex('new'); // Highlight "New Frame" box after saving
      setJobStatus('ready');
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save frame');
      setJobStatus('error');
    }
  };

  // Select a frame to view/edit
  const selectFrame = (index) => {
    setSelectedFrameIndex(index);
    setCanvasPreviewPlaying(false);
    
    if (index >= 0 && stickFrames[index]) {
      loadFrameToCanvas(index);
    } else {
      // New frame - clear canvas
      if (canvasRef.current && contextRef.current) {
        contextRef.current.fillStyle = 'white';
        contextRef.current.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        setCanvasHistory([canvasRef.current.toDataURL()]);
      }
    }
  };

  // Start editing a frame
  const startEditingFrame = (index) => {
    setEditingFrameIndex(index);
    setSelectedFrameIndex(index);
    loadFrameToCanvas(index);
  };

  // Delete a frame
  const deleteFrame = async (index) => {
    if (!jobId) return;
    
    try {
      await axios.delete(`${API}/api/motion-lab/v3/job/${jobId}/frame/${index}`);
      setStickFrames(prev => prev.filter((_, i) => i !== index).map((f, i) => ({...f, index: i})));
      if (selectedFrameIndex === index) {
        setSelectedFrameIndex(-1);
        clearCanvas();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete frame');
    }
  };

  // Toggle canvas preview animation
  const toggleCanvasPreview = () => {
    if (stickFrames.length === 0) return;
    setCanvasPreviewPlaying(!canvasPreviewPlaying);
    if (!canvasPreviewPlaying) {
      setCanvasPreviewFrame(0);
    }
  };

  // Preview in preview mode
  const togglePreview = () => {
    if (previewPlaying) {
      clearInterval(previewIntervalRef.current);
      setPreviewPlaying(false);
    } else {
      setPreviewPlaying(true);
      previewIntervalRef.current = setInterval(() => {
        setPreviewFrame(prev => {
          const maxFrame = stickFrames.length;
          return (prev + 1) % (maxFrame + 1);
        });
      }, 500);
    }
  };

  useEffect(() => {
    return () => {
      if (previewIntervalRef.current) {
        clearInterval(previewIntervalRef.current);
      }
    };
  }, []);

  // Generate all frames from stick figures
  const generateAllFrames = async () => {
    if (!jobId || stickFrames.length === 0) return;
    
    setJobStatus('generating_all');
    setMode('generating');
    
    try {
      await axios.post(`${API}/api/motion-lab/v3/job/${jobId}/generate-all-frames`);
      
      // Fetch updated job state
      const jobResp = await axios.get(`${API}/api/motion-lab/v3/job/${jobId}`);
      
      // Update stick frames with generated URLs (map snake_case to camelCase)
      const updatedFrames = jobResp.data.stick_frames.map((sf, i) => ({
        ...stickFrames[i],
        index: sf.index,
        stickUrl: sf.stick_figure_url || stickFrames[i]?.stickUrl,
        generatedUrl: sf.generated_url || null, // Map snake_case to camelCase
        status: sf.status || 'pending',
        canvasData: stickFrames[i]?.canvasData // Preserve canvas data
      }));
      setStickFrames(updatedFrames);
      
      setJobStatus('frames_complete');
      setMode('final');
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate frames');
      setJobStatus('error');
    }
  };

  // Regenerate a single frame
  const regenerateFrame = async (index) => {
    if (!jobId) return;
    
    setStickFrames(prev => prev.map((f, i) => 
      i === index ? {...f, status: 'generating'} : f
    ));
    
    try {
      const resp = await axios.post(`${API}/api/motion-lab/v3/job/${jobId}/generate-frame/${index}`);
      
      setStickFrames(prev => prev.map((f, i) => 
        i === index ? {...f, generatedUrl: resp.data.generated_url, status: 'complete'} : f
      ));
      
    } catch (err) {
      setStickFrames(prev => prev.map((f, i) => 
        i === index ? {...f, status: 'error'} : f
      ));
      setError(err.response?.data?.detail || 'Failed to regenerate frame');
    }
  };

  // Run FILM interpolation
  const runInterpolation = async () => {
    if (!jobId) return;
    
    setJobStatus('interpolating');
    
    try {
      await axios.post(
        `${API}/api/motion-lab/v3/job/${jobId}/interpolate`,
        null,
        { params: { times_to_interpolate: 3 }, timeout: 300000 }
      );
      
      // Compile video
      setJobStatus('compiling');
      const videoResp = await axios.post(`${API}/api/motion-lab/v3/job/${jobId}/compile-video`);
      
      setFinalVideoUrl(videoResp.data.video_url);
      setJobStatus('complete');
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Interpolation failed');
      setJobStatus('error');
    }
  };

  // Get the image to show as base layer while drawing
  const getBaseImage = () => {
    // If editing an existing frame, show the previous frame or starting frame
    if (editingFrameIndex !== null) {
      if (editingFrameIndex === 0) {
        return startingFrame;
      }
      return stickFrames[editingFrameIndex - 1]?.generatedUrl || 
             stickFrames[editingFrameIndex - 1]?.stickUrl ||
             startingFrame;
    }
    
    // For new frame, show the last generated/stick frame or starting frame
    if (stickFrames.length > 0) {
      const lastFrame = stickFrames[stickFrames.length - 1];
      return lastFrame.generatedUrl || lastFrame.stickUrl || startingFrame;
    }
    return startingFrame;
  };

  // Skeleton management
  const defaultSkeleton = {
    head: { x: 640, y: 100 },
    neck: { x: 640, y: 150 },
    leftShoulder: { x: 580, y: 180 },
    rightShoulder: { x: 700, y: 180 },
    leftElbow: { x: 520, y: 250 },
    rightElbow: { x: 760, y: 250 },
    leftWrist: { x: 460, y: 320 },
    rightWrist: { x: 820, y: 320 },
    spine: { x: 640, y: 280 },
    leftHip: { x: 600, y: 350 },
    rightHip: { x: 680, y: 350 },
    leftKnee: { x: 590, y: 450 },
    rightKnee: { x: 690, y: 450 },
    leftAnkle: { x: 580, y: 550 },
    rightAnkle: { x: 700, y: 550 }
  };

  const skeletonConnections = [
    ['head', 'neck'],
    ['neck', 'leftShoulder'], ['neck', 'rightShoulder'],
    ['leftShoulder', 'leftElbow'], ['leftElbow', 'leftWrist'],
    ['rightShoulder', 'rightElbow'], ['rightElbow', 'rightWrist'],
    ['neck', 'spine'],
    ['spine', 'leftHip'], ['spine', 'rightHip'],
    ['leftHip', 'leftKnee'], ['leftKnee', 'leftAnkle'],
    ['rightHip', 'rightKnee'], ['rightKnee', 'rightAnkle']
  ];

  // State for pose detection loading
  const [detectingPoses, setDetectingPoses] = useState(false);

  // Initialize skeletons with pose detection from starting frame
  const initializeSkeletons = async () => {
    if (!jobId || !startingFrame) {
      // Fallback to default positions if no starting frame
      const validChars = characters.filter(c => c.name);
      const newSkeletons = {};
      
      validChars.forEach((char, index) => {
        const offsetX = validChars.length > 1 
          ? (index - (validChars.length - 1) / 2) * 300 
          : 0;
        
        const skeleton = {};
        Object.entries(defaultSkeleton).forEach(([joint, pos]) => {
          skeleton[joint] = { x: pos.x + offsetX, y: pos.y };
        });
        skeleton.color = char.color;
        
        newSkeletons[char.name] = skeleton;
      });
      
      setSkeletons(newSkeletons);
      return;
    }

    // Try to detect poses from starting frame
    setDetectingPoses(true);
    try {
      const resp = await axios.post(`${API}/api/motion-lab/v3/job/${jobId}/detect-poses`);
      
      if (resp.data.poses) {
        // Add colors to detected poses
        const posesWithColors = {};
        for (const [charName, joints] of Object.entries(resp.data.poses)) {
          posesWithColors[charName] = {
            ...joints,
            color: characters.find(c => c.name === charName)?.color || DEFAULT_COLORS[0]
          };
        }
        setSkeletons(posesWithColors);
      }
    } catch (err) {
      console.error('Pose detection failed, using defaults:', err);
      // Fallback to default positions
      const validChars = characters.filter(c => c.name);
      const newSkeletons = {};
      
      validChars.forEach((char, index) => {
        const offsetX = validChars.length > 1 
          ? (index - (validChars.length - 1) / 2) * 300 
          : 0;
        
        const skeleton = {};
        Object.entries(defaultSkeleton).forEach(([joint, pos]) => {
          skeleton[joint] = { x: pos.x + offsetX, y: pos.y };
        });
        skeleton.color = char.color;
        
        newSkeletons[char.name] = skeleton;
      });
      
      setSkeletons(newSkeletons);
    }
    setDetectingPoses(false);
  };

  // Draw skeletons on canvas
  const drawSkeletonsOnCanvas = useCallback(() => {
    if (!contextRef.current || !canvasRef.current) return;
    
    const context = contextRef.current;
    const canvas = canvasRef.current;
    
    // Clear and fill white
    context.fillStyle = 'white';
    context.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw each skeleton
    Object.entries(skeletons).forEach(([name, skeleton]) => {
      const color = skeleton.color || currentColor;
      context.strokeStyle = color;
      context.lineWidth = brushSize;
      
      // Draw connections
      skeletonConnections.forEach(([from, to]) => {
        if (skeleton[from] && skeleton[to]) {
          context.beginPath();
          context.moveTo(skeleton[from].x, skeleton[from].y);
          context.lineTo(skeleton[to].x, skeleton[to].y);
          context.stroke();
        }
      });
      
      // Draw joints
      context.fillStyle = color;
      Object.entries(skeleton).forEach(([joint, pos]) => {
        if (joint !== 'color' && pos.x !== undefined) {
          context.beginPath();
          context.arc(pos.x, pos.y, 8, 0, Math.PI * 2);
          context.fill();
        }
      });
    });
    
    // Update history
    setCanvasHistory([canvas.toDataURL()]);
  }, [skeletons, currentColor, brushSize]);

  // Handle joint dragging
  const [draggingJoint, setDraggingJoint] = useState(null);
  const [dragStartPos, setDragStartPos] = useState(null);

  const handleCanvasMouseDown = (e) => {
    if (canvasPreviewPlaying) return;
    
    if (drawingTool === 'freehand' || drawingTool === 'eraser') {
      startDrawing(e);
      return;
    }
    
    // Skeleton mode - check if clicking on a joint
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    // Find closest joint
    for (const [charName, skeleton] of Object.entries(skeletons)) {
      for (const [jointName, pos] of Object.entries(skeleton)) {
        if (jointName === 'color') continue;
        const dist = Math.sqrt((pos.x - x) ** 2 + (pos.y - y) ** 2);
        if (dist < 20) {
          setDraggingJoint({ character: charName, joint: jointName, shiftKey: e.shiftKey });
          setDragStartPos({ x, y });
          return;
        }
      }
    }
  };

  const handleCanvasMouseMove = (e) => {
    if (canvasPreviewPlaying) return;
    
    if (drawingTool === 'freehand' || drawingTool === 'eraser') {
      draw(e);
      return;
    }
    
    if (!draggingJoint || !dragStartPos) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    // If shift is held, move the entire skeleton
    if (draggingJoint.shiftKey) {
      const dx = x - dragStartPos.x;
      const dy = y - dragStartPos.y;
      
      setSkeletons(prev => {
        const skeleton = prev[draggingJoint.character];
        const newSkeleton = { color: skeleton.color };
        
        // Move all joints by the delta
        for (const [jointName, pos] of Object.entries(skeleton)) {
          if (jointName === 'color') continue;
          newSkeleton[jointName] = { 
            x: pos.x + dx, 
            y: pos.y + dy 
          };
        }
        
        return {
          ...prev,
          [draggingJoint.character]: newSkeleton
        };
      });
      
      setDragStartPos({ x, y });
    } else {
      // Move just this joint
      setSkeletons(prev => ({
        ...prev,
        [draggingJoint.character]: {
          ...prev[draggingJoint.character],
          [draggingJoint.joint]: { x, y }
        }
      }));
    }
  };

  const handleCanvasMouseUp = () => {
    if (drawingTool === 'freehand' || drawingTool === 'eraser') {
      stopDrawing();
    } else {
      setDraggingJoint(null);
      setDragStartPos(null);
    }
    
    // Redraw skeletons if in skeleton mode
    if (drawingTool === 'skeleton') {
      drawSkeletonsOnCanvas();
    }
  };

  // Redraw skeletons when they change
  useEffect(() => {
    if (drawingTool === 'skeleton' && Object.keys(skeletons).length > 0) {
      drawSkeletonsOnCanvas();
    }
  }, [skeletons, drawingTool, drawSkeletonsOnCanvas]);


  return (
    <div className="min-h-screen bg-zinc-950 text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
            <Pencil className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Motion Lab</h1>
            <p className="text-zinc-400 text-sm">v3 - Stick Figure Guided Animation</p>
          </div>
          <Badge variant="outline" className="ml-2 border-green-500 text-green-500">
            Draw Your Poses
          </Badge>
        </div>

        {/* Progress Steps - Simplified workflow */}
        <div className="flex gap-2 mb-6">
          {['Setup', 'Draw & Generate', 'Export'].map((step, i) => (
            <Badge 
              key={step}
              variant={
                (mode === 'setup' && i === 0) ||
                (mode === 'drawing' && i === 1) ||
                (mode === 'final' && i === 2)
                  ? 'default' : 'secondary'
              }
              className="px-3 py-1"
            >
              {i + 1}. {step}
            </Badge>
          ))}
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-4 p-3 bg-red-900/50 border border-red-500 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-200">{error}</span>
            <Button variant="ghost" size="sm" onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* SETUP MODE */}
        {mode === 'setup' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Characters */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Characters
                </CardTitle>
                <Button variant="outline" size="sm" onClick={addCharacter}>
                  <Plus className="w-4 h-4 mr-1" /> Add Character
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {characters.map((char, charIndex) => (
                  <div key={charIndex} className="p-4 bg-zinc-800 rounded-lg space-y-3">
                    <div className="flex items-center gap-3">
                      <Input
                        placeholder="Character name (e.g., Dean)"
                        value={char.name}
                        onChange={(e) => updateCharacter(charIndex, 'name', e.target.value)}
                        className="flex-1 bg-zinc-700 border-zinc-600"
                      />
                      <input
                        type="color"
                        value={char.color}
                        onChange={(e) => updateCharacter(charIndex, 'color', e.target.value)}
                        className="w-10 h-10 rounded cursor-pointer"
                        title="Stick figure color"
                      />
                      {characters.length > 1 && (
                        <Button variant="ghost" size="icon" onClick={() => removeCharacter(charIndex)}>
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      {char.images.map((img, imgIndex) => (
                        <div key={imgIndex} className="relative w-16 h-16 rounded overflow-hidden group">
                          <img src={img.preview} alt="" className="w-full h-full object-cover" />
                          <button
                            onClick={() => removeCharacterImage(charIndex, imgIndex)}
                            className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <label className="w-16 h-16 border-2 border-dashed border-zinc-600 rounded flex items-center justify-center cursor-pointer hover:border-zinc-500">
                        <Plus className="w-5 h-5 text-zinc-500" />
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => addCharacterImage(charIndex, e)}
                          className="hidden"
                        />
                      </label>
                    </div>
                    <p className="text-xs text-zinc-500">
                      Color: <span style={{color: char.color}}>{char.color}</span> - Used for stick figure
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Background & Action */}
            <div className="space-y-6">
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Palette className="w-5 h-5" />
                    Background (Optional)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {backgroundImages.map((img, i) => (
                      <div key={i} className="relative w-20 h-12 rounded overflow-hidden group">
                        <img src={img.preview} alt="" className="w-full h-full object-cover" />
                        <button
                          onClick={() => setBackgroundImages(prev => prev.filter((_, idx) => idx !== i))}
                          className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <label className="w-20 h-12 border-2 border-dashed border-zinc-600 rounded flex items-center justify-center cursor-pointer hover:border-zinc-500">
                      <Plus className="w-5 h-5 text-zinc-500" />
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={addBackgroundImage}
                        className="hidden"
                      />
                    </label>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ImageIcon className="w-5 h-5" />
                    Starting Frame
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Option 1: Upload your own image */}
                  <div className="p-4 bg-zinc-800 rounded-lg space-y-3">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      <Upload className="w-4 h-4" />
                      Option 1: Upload Your Own Image
                    </h4>
                    <div className="flex items-center gap-3">
                      {uploadedStartingFrame ? (
                        <div className="relative w-32 h-20 rounded overflow-hidden group">
                          <img 
                            src={uploadedStartingFrame.preview} 
                            alt="Starting frame" 
                            className="w-full h-full object-cover" 
                          />
                          <button
                            onClick={() => setUploadedStartingFrame(null)}
                            className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <label className="flex-1 h-20 border-2 border-dashed border-zinc-600 rounded flex items-center justify-center cursor-pointer hover:border-zinc-500">
                          <div className="text-center">
                            <Upload className="w-5 h-5 mx-auto text-zinc-500" />
                            <span className="text-xs text-zinc-500">Click to upload</span>
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onload = (event) => {
                                  setUploadedStartingFrame({
                                    file,
                                    preview: event.target.result
                                  });
                                  setActionPrompt(''); // Clear AI prompt when uploading
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                            className="hidden"
                          />
                        </label>
                      )}
                      {uploadedStartingFrame && (
                        <Button 
                          onClick={startWithUploadedImage}
                          disabled={jobStatus !== 'idle' && jobStatus !== 'error'}
                        >
                          {jobStatus === 'uploading' ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <ChevronRight className="w-4 h-4 mr-1" />
                          )}
                          Start Drawing
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-zinc-700"></div>
                    <span className="text-zinc-500 text-sm">OR</span>
                    <div className="flex-1 h-px bg-zinc-700"></div>
                  </div>

                  {/* Option 2: AI Generate */}
                  <div className="p-4 bg-zinc-800 rounded-lg space-y-3">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      <Wand2 className="w-4 h-4" />
                      Option 2: AI Generate Starting Frame
                    </h4>
                    <Textarea
                      placeholder="Describe the starting pose (e.g., 'Dean and Buk face each other in fighting stances')"
                      value={actionPrompt}
                      onChange={(e) => {
                        setActionPrompt(e.target.value);
                        setUploadedStartingFrame(null); // Clear upload when typing
                      }}
                      className="bg-zinc-700 border-zinc-600 min-h-[80px]"
                    />
                    <Button 
                      className="w-full" 
                      onClick={startWorkflow}
                      disabled={(jobStatus !== 'idle' && jobStatus !== 'error') || !actionPrompt.trim()}
                    >
                      {jobStatus === 'uploading' ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading Images...</>
                      ) : jobStatus === 'creating' ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating Job...</>
                      ) : jobStatus === 'generating_start' ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                      ) : (
                        <><Wand2 className="w-4 h-4 mr-2" /> Generate with AI</>
                      )}
                    </Button>
                  </div>

                  {/* Skip option */}
                  <div className="text-center">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={skipToDrawing}
                      className="text-zinc-500 hover:text-zinc-300"
                    >
                      Skip to drawing (no starting frame)
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* DRAWING MODE */}
        {mode === 'drawing' && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Drawing Canvas */}
            <div className="lg:col-span-3 space-y-4">
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader className="flex flex-row items-center justify-between py-3">
                  <CardTitle className="text-lg">
                    {editingFrameIndex !== null ? `Editing Frame ${editingFrameIndex + 1}` : 'Draw New Frame'}
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant={drawingTool === 'freehand' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setDrawingTool('freehand')}
                    >
                      <Pencil className="w-4 h-4 mr-1" /> Freehand
                    </Button>
                    <Button
                      variant={drawingTool === 'eraser' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setDrawingTool('eraser')}
                    >
                      <Eraser className="w-4 h-4 mr-1" /> Eraser
                    </Button>
                    <Button
                      variant={drawingTool === 'skeleton' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setDrawingTool('skeleton');
                        initializeSkeletons();
                      }}
                      disabled={detectingPoses}
                    >
                      {detectingPoses ? (
                        <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Detecting...</>
                      ) : (
                        <><Move className="w-4 h-4 mr-1" /> Skeleton</>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="relative bg-zinc-800 rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
                    {/* Base image layer - faded to white */}
                    {showBaseImage && getBaseImage() && !canvasPreviewPlaying && (
                      <div className="absolute inset-0">
                        <img
                          src={getBaseImage()}
                          alt="Base"
                          className="w-full h-full object-contain"
                          style={{ opacity: baseImageOpacity }}
                        />
                        {/* White overlay for fade effect */}
                        <div 
                          className="absolute inset-0 bg-white pointer-events-none"
                          style={{ opacity: 1 - baseImageOpacity }}
                        />
                      </div>
                    )}
                    
                    {/* Onion skin layers */}
                    {!canvasPreviewPlaying && onionSkinCount > 0 && stickFrames.slice(-onionSkinCount).map((frame, i) => (
                      <img
                        key={i}
                        src={frame.canvasData || frame.stickUrl}
                        alt={`Onion ${i}`}
                        className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                        style={{ opacity: 0.15 * (i + 1) / onionSkinCount, mixBlendMode: 'multiply' }}
                      />
                    ))}
                    
                    {/* Drawing canvas */}
                    <canvas
                      ref={canvasRef}
                      className={`absolute inset-0 w-full h-full ${
                        canvasPreviewPlaying ? 'cursor-not-allowed' : 
                        drawingTool === 'eraser' ? 'cursor-cell' : 'cursor-crosshair'
                      }`}
                      style={{ mixBlendMode: canvasPreviewPlaying ? 'normal' : 'multiply' }}
                      onMouseDown={handleCanvasMouseDown}
                      onMouseMove={handleCanvasMouseMove}
                      onMouseUp={handleCanvasMouseUp}
                      onMouseLeave={handleCanvasMouseUp}
                    />
                    
                    {/* Preview indicator */}
                    {canvasPreviewPlaying && (
                      <div className="absolute top-4 left-4 px-3 py-1 bg-purple-600 rounded-full text-sm flex items-center gap-2">
                        <Play className="w-4 h-4" />
                        Frame {canvasPreviewFrame + 1} / {stickFrames.length + 1}
                      </div>
                    )}
                  </div>
                  
                  {/* Drawing Controls */}
                  <div className="flex items-center gap-4 mt-4 flex-wrap">
                    {/* Color picker for freehand */}
                    {drawingTool === 'freehand' && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-zinc-400">Color:</span>
                        {characters.filter(c => c.name).map((char, i) => (
                          <button
                            key={i}
                            onClick={() => setCurrentColor(char.color)}
                            className={`w-8 h-8 rounded-full border-2 ${currentColor === char.color ? 'border-white' : 'border-transparent'}`}
                            style={{ backgroundColor: char.color }}
                            title={char.name}
                          />
                        ))}
                      </div>
                    )}
                    
                    {/* Brush/Eraser size */}
                    {(drawingTool === 'freehand' || drawingTool === 'eraser') && (
                      <div className="flex items-center gap-2 flex-1 max-w-[200px]">
                        <span className="text-sm text-zinc-400">
                          {drawingTool === 'eraser' ? 'Eraser:' : 'Size:'}
                        </span>
                        <Slider
                          value={[drawingTool === 'eraser' ? eraserSize : brushSize]}
                          onValueChange={([v]) => drawingTool === 'eraser' ? setEraserSize(v) : setBrushSize(v)}
                          min={drawingTool === 'eraser' ? 5 : 1}
                          max={drawingTool === 'eraser' ? 50 : 20}
                          step={1}
                          className="flex-1"
                        />
                      </div>
                    )}
                    
                    {/* Actions */}
                    <div className="flex gap-2 ml-auto">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={toggleCanvasPreview}
                        disabled={stickFrames.length === 0}
                      >
                        {canvasPreviewPlaying ? <Pause className="w-4 h-4 mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                        {canvasPreviewPlaying ? 'Stop' : 'Preview'}
                      </Button>
                      <Button variant="outline" size="sm" onClick={undoDrawing} disabled={canvasPreviewPlaying}>
                        <Undo className="w-4 h-4 mr-1" /> Undo
                      </Button>
                      <Button variant="outline" size="sm" onClick={clearCanvas} disabled={canvasPreviewPlaying}>
                        <Eraser className="w-4 h-4 mr-1" /> Clear
                      </Button>
                      {editingFrameIndex !== null && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => {
                            setEditingFrameIndex(null);
                            setSelectedFrameIndex(-1);
                            clearCanvas();
                          }}
                        >
                          <X className="w-4 h-4 mr-1" /> Cancel Edit
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        onClick={saveStickFrame} 
                        disabled={jobStatus === 'saving_frame' || canvasPreviewPlaying}
                      >
                        {jobStatus === 'saving_frame' ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4 mr-1" />
                        )}
                        {editingFrameIndex !== null ? 'Update Frame' : 'Save Frame'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Frame Strip */}
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span>Frames ({stickFrames.length + 1} total) - Hold Shift + drag to move entire skeleton</span>
                    <div className="flex gap-2">
                      <Button 
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          // Generate all ungenerated frames
                          stickFrames.forEach((frame, i) => {
                            if (!frame.generatedUrl && !generatingFrames[i]) {
                              generateSingleFrame(i);
                            }
                          });
                        }}
                        disabled={stickFrames.length === 0 || stickFrames.every(f => f.generatedUrl)}
                      >
                        <Sparkles className="w-4 h-4 mr-1" />
                        Generate All
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={() => setMode('final')}
                        disabled={stickFrames.length === 0 || !stickFrames.some(f => f.generatedUrl)}
                      >
                        Export Video <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {/* Starting frame */}
                    <div 
                      className={`shrink-0 w-32 cursor-pointer ${
                        selectedFrameIndex === -1 ? 'ring-2 ring-purple-500' : ''
                      }`}
                      onClick={() => selectFrame(-1)}
                    >
                      <div className="h-20 rounded overflow-hidden border-2 border-green-500">
                        {startingFrame ? (
                          <img src={startingFrame} alt="Start" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                            <ImageIcon className="w-6 h-6 text-zinc-600" />
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-center text-zinc-400 mt-1">Start (ref)</div>
                    </div>
                    
                    {/* Stick frames */}
                    {stickFrames.map((frame, i) => (
                      <div 
                        key={i}
                        className={`shrink-0 w-40 relative group cursor-pointer ${
                          selectedFrameIndex === i ? 'ring-2 ring-purple-500 rounded' : ''
                        }`}
                        onClick={() => selectFrame(i)}
                        onDoubleClick={() => startEditingFrame(i)}
                      >
                        {/* Two-row frame: stick figure on top, generated below */}
                        <div className="space-y-1">
                          {/* Stick figure thumbnail */}
                          <div className="h-16 rounded overflow-hidden border-2 border-zinc-700 hover:border-zinc-500 relative">
                            <img 
                              src={frame.canvasData || frame.stickUrl} 
                              alt={`Stick ${i + 1}`} 
                              className="w-full h-full object-cover bg-white"
                            />
                            <div className="absolute top-0.5 left-0.5 text-[10px] bg-black/50 px-1 rounded">Stick</div>
                          </div>
                          
                          {/* Generated image thumbnail */}
                          <div className="h-16 rounded overflow-hidden border-2 border-zinc-700 relative">
                            {generatingFrames[i] ? (
                              <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                                <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
                              </div>
                            ) : frame.generatedUrl ? (
                              <img 
                                src={frame.generatedUrl} 
                                alt={`Generated ${i + 1}`} 
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <button
                                onClick={(e) => { e.stopPropagation(); generateSingleFrame(i); }}
                                className="w-full h-full bg-zinc-800 flex flex-col items-center justify-center hover:bg-zinc-700 transition-colors"
                                title="Generate this frame"
                              >
                                <Sparkles className="w-4 h-4 text-purple-400" />
                                <span className="text-[10px] text-zinc-400 mt-0.5">Generate</span>
                              </button>
                            )}
                            {frame.generatedUrl && (
                              <div className="absolute top-0.5 left-0.5 text-[10px] bg-green-600/80 px-1 rounded">Done</div>
                            )}
                            {/* Download/Upload buttons for generated frame */}
                            {frame.generatedUrl && (
                              <div className="absolute bottom-0.5 right-0.5 flex gap-0.5">
                                <button
                                  onClick={(e) => { e.stopPropagation(); downloadGeneratedFrame(i); }}
                                  className="w-4 h-4 bg-black/60 rounded flex items-center justify-center hover:bg-black/80"
                                  title="Download generated frame"
                                >
                                  <Download className="w-2.5 h-2.5" />
                                </button>
                                <label className="w-4 h-4 bg-black/60 rounded flex items-center justify-center hover:bg-black/80 cursor-pointer" title="Upload custom frame">
                                  <Upload className="w-2.5 h-2.5" />
                                  <input 
                                    type="file" 
                                    className="hidden" 
                                    accept="image/*"
                                    onChange={(e) => { 
                                      e.stopPropagation();
                                      if (e.target.files[0]) uploadGeneratedFrame(i, e.target.files[0]); 
                                    }}
                                  />
                                </label>
                              </div>
                            )}
                            {!frame.generatedUrl && !generatingFrames[i] && (
                              <label className="absolute bottom-0.5 right-0.5 w-4 h-4 bg-black/60 rounded flex items-center justify-center hover:bg-black/80 cursor-pointer" title="Upload custom frame">
                                <Upload className="w-2.5 h-2.5" />
                                <input 
                                  type="file" 
                                  className="hidden" 
                                  accept="image/*"
                                  onChange={(e) => { 
                                    e.stopPropagation();
                                    if (e.target.files[0]) uploadGeneratedFrame(i, e.target.files[0]); 
                                  }}
                                />
                              </label>
                            )}
                          </div>
                        </div>
                        
                        {/* Action buttons on hover */}
                        <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100">
                          <button
                            onClick={(e) => { e.stopPropagation(); startEditingFrame(i); }}
                            className="w-5 h-5 bg-blue-500 rounded flex items-center justify-center"
                            title="Edit stick figure"
                          >
                            <Edit3 className="w-3 h-3" />
                          </button>
                          {frame.generatedUrl && (
                            <button
                              onClick={(e) => { e.stopPropagation(); generateSingleFrame(i); }}
                              className="w-5 h-5 bg-purple-500 rounded flex items-center justify-center"
                              title="Regenerate"
                            >
                              <RefreshCw className="w-3 h-3" />
                            </button>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteFrame(i); }}
                            className="w-5 h-5 bg-red-500 rounded flex items-center justify-center"
                            title="Delete frame"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="text-xs text-center text-zinc-400 mt-1">Frame {i + 1}</div>
                      </div>
                    ))}
                    
                    {/* Add new frame button */}
                    <div 
                      className={`shrink-0 w-40 cursor-pointer ${
                        selectedFrameIndex === 'new' || selectedFrameIndex === -1 ? 'ring-2 ring-purple-500 rounded' : ''
                      }`}
                      onClick={() => {
                        setSelectedFrameIndex('new');
                        setEditingFrameIndex(null);
                        clearCanvas();
                      }}
                    >
                      <div className={`h-[136px] rounded border-2 border-dashed ${
                        selectedFrameIndex === 'new' || selectedFrameIndex === -1 
                          ? 'border-purple-500' 
                          : 'border-zinc-600 hover:border-zinc-500'
                      } flex items-center justify-center`}>
                        <Plus className={`w-6 h-6 ${
                          selectedFrameIndex === 'new' || selectedFrameIndex === -1 
                            ? 'text-purple-400' 
                            : 'text-zinc-500'
                        }`} />
                      </div>
                      <div className="text-xs text-center text-zinc-400 mt-1">New Frame</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Settings Panel */}
            <div className="space-y-4">
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-sm">Display Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-zinc-400">Show Base Image</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowBaseImage(!showBaseImage)}
                      >
                        {showBaseImage ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </Button>
                    </div>
                    {showBaseImage && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-zinc-500">Opacity:</span>
                        <Slider
                          value={[baseImageOpacity * 100]}
                          onValueChange={([v]) => setBaseImageOpacity(v / 100)}
                          min={10}
                          max={80}
                          className="flex-1"
                        />
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <span className="text-sm text-zinc-400">Onion Skin</span>
                    <div className="flex items-center gap-2 mt-2">
                      {[0, 1, 2, 3].map(n => (
                        <Button
                          key={n}
                          variant={onionSkinCount === n ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setOnionSkinCount(n)}
                        >
                          {n}
                        </Button>
                      ))}
                    </div>
                    <p className="text-xs text-zinc-500 mt-1">Previous frames to show</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-sm">Character Colors</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {characters.filter(c => c.name).map((char, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: char.color }}
                        />
                        <span className="text-sm">{char.name}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              {startingDescription && (
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-sm">Starting Pose</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-zinc-400">{startingDescription}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* FINAL MODE */}
        {mode === 'final' && (
          <div className="space-y-6">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Film className="w-5 h-5" />
                  Generated Frames
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {/* Starting frame */}
                  {startingFrame && (
                    <div className="space-y-1">
                      <div className="aspect-video rounded overflow-hidden border-2 border-green-500">
                        <img 
                          src={startingFrame} 
                          alt="Start" 
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                      <p className="text-xs text-zinc-400 text-center">Start</p>
                    </div>
                  )}
                  
                  {/* Generated frames */}
                  {stickFrames.map((frame, i) => (
                    <div key={i} className="space-y-1 group">
                      <div className={`aspect-video rounded overflow-hidden border-2 ${
                        frame.status === 'complete' && frame.generatedUrl ? 'border-green-500' : 
                        frame.status === 'generating' ? 'border-yellow-500' : 
                        frame.status === 'error' ? 'border-red-500' : 'border-zinc-700'
                      }`}>
                        {frame.status === 'generating' ? (
                          <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                            <Loader2 className="w-6 h-6 animate-spin text-yellow-500" />
                          </div>
                        ) : frame.generatedUrl ? (
                          <img 
                            src={frame.generatedUrl} 
                            alt={`Frame ${i + 1}`} 
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              // Fallback to stick figure if generated fails
                              e.target.src = frame.canvasData || frame.stickUrl;
                            }}
                          />
                        ) : (
                          <img 
                            src={frame.canvasData || frame.stickUrl} 
                            alt={`Stick ${i + 1}`} 
                            className="w-full h-full object-cover bg-white" 
                          />
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-zinc-400">Frame {i + 1}</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 opacity-0 group-hover:opacity-100"
                          onClick={() => regenerateFrame(i)}
                          disabled={frame.status === 'generating'}
                        >
                          <RefreshCw className={`w-3 h-3 ${frame.status === 'generating' ? 'animate-spin' : ''}`} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="flex gap-4 mt-6">
                  <Button variant="outline" onClick={() => setMode('drawing')}>
                    <ChevronLeft className="w-4 h-4 mr-1" /> Add More Frames
                  </Button>
                  <Button 
                    className="flex-1" 
                    size="lg"
                    onClick={runInterpolation}
                    disabled={jobStatus === 'interpolating' || jobStatus === 'compiling'}
                  >
                    {jobStatus === 'interpolating' ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running FILM Interpolation...</>
                    ) : jobStatus === 'compiling' ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Compiling Video...</>
                    ) : (
                      <><Film className="w-4 h-4 mr-2" /> Finalize with FILM</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            {/* Final Video */}
            {finalVideoUrl && (
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    Final Animation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative bg-black rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
                    <video 
                      src={finalVideoUrl} 
                      controls 
                      autoPlay 
                      loop
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="flex items-center gap-2 mt-4 p-3 bg-green-900/30 rounded-lg">
                    <Check className="w-5 h-5 text-green-500" />
                    <span className="text-green-400">Animation complete with FILM interpolation!</span>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
